import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const ScenarioContext = createContext();

export const useScenario = () => {
  const context = useContext(ScenarioContext);
  if (!context) {
    throw new Error('useScenario must be used within a ScenarioProvider');
  }
  return context;
};

export const ScenarioProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real-time listener for scenarios
  useEffect(() => {
    if (!currentUser) {
      setScenarios([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(
      collection(db, 'scenarios'),
      where('userId', '==', currentUser.uid),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const scenariosData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setScenarios(scenariosData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching scenarios:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [currentUser]);

  const saveScenario = async (name, description, data) => {
    try {
      setError(null);
      const scenario = {
        name,
        description,
        data,
        userId: currentUser.uid,
        timestamp: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'scenarios'), scenario);
      return { success: true, id: docRef.id };
    } catch (err) {
      console.error('Error saving scenario:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const updateScenario = async (scenarioId, updates) => {
    try {
      setError(null);
      const scenarioRef = doc(db, 'scenarios', scenarioId);
      await updateDoc(scenarioRef, {
        ...updates,
        timestamp: serverTimestamp()
      });
      return { success: true };
    } catch (err) {
      console.error('Error updating scenario:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const deleteScenario = async (scenarioId) => {
    try {
      setError(null);
      await deleteDoc(doc(db, 'scenarios', scenarioId));
      return { success: true };
    } catch (err) {
      console.error('Error deleting scenario:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const loadScenario = (scenario) => {
    // Returns scenario data for the calling component to apply
    return scenario.data;
  };

  const value = {
    scenarios,
    loading,
    error,
    saveScenario,
    updateScenario,
    deleteScenario,
    loadScenario
  };

  return (
    <ScenarioContext.Provider value={value}>
      {children}
    </ScenarioContext.Provider>
  );
};
